export type NormalizedOption = {
  value: any;
  label: string;
  disabled?: boolean;
  raw?: any;
};

export type StaticOptions = any[];
export type AsyncOptionsFn = (query: string) => Promise<any[]>;

export function normalizeOptionsInput(
  opts: any,
): StaticOptions | AsyncOptionsFn | undefined {
  if (!opts || typeof opts === 'function' || Array.isArray(opts)) {
    return opts;
  }
  if (typeof opts === 'object') {
    if (Array.isArray(opts.data)) {
      return opts.data;
    }
    return Object.entries(opts).map(([key, value]) => ({
      value: /^\d+$/.test(key) ? Number(key) : key,
      text: value,
    }));
  }
  return opts;
}

export type DataSource =
  | { name: string; parameters?: Record<string, any> }
  | (() => { name: string; parameters?: Record<string, any> });

export type RequestFn = (
  name: string,
  parameters: Record<string, any>,
) => Promise<any>;

export type ProviderConfig = {
  options?: StaticOptions | AsyncOptionsFn;
  initialOptions?: any[];
  dataSource?: DataSource;
  prefetch?: boolean;
  valueField?: string;
  textField?: string;
  disabledField?: string;
  request?: RequestFn;
};

function getByPath(obj: any, path: string): any {
  if (obj == null || typeof obj !== 'object') {
    return undefined;
  }
  if (!path.includes('.')) {
    return obj[path];
  }
  return path.split('.').reduce((acc: any, key: string) => acc?.[key], obj);
}

export function useItpSelectProvider(config: ProviderConfig) {
  const effectiveOptions = normalizeOptionsInput(config.options);
  let searchCount = 0;

  function toNormalized(item: any): NormalizedOption {
    const isPrimitive = item === null || typeof item !== 'object';
    const vf = config.valueField ?? 'value';
    const tf = config.textField ?? 'text';
    const value = isPrimitive ? item : getByPath(item, vf);
    const rawLabel = isPrimitive ? item : getByPath(item, tf);
    const label = rawLabel != null ? String(rawLabel) : '';
    const disabled = config.disabledField
      ? !!item?.[config.disabledField]
      : false;
    return { value, label, disabled, raw: item };
  }

  function isAsyncOptions(): boolean {
    return typeof effectiveOptions === 'function';
  }

  function resolveDataSource(): {
    name: string;
    parameters?: Record<string, any>;
  } | null {
    let ds: any = config.dataSource;
    let guard = 0;
    while (typeof ds === 'function' && guard++ < 5) {
      ds = ds();
    }
    return ds ?? null;
  }

  function hasDataSource(): boolean {
    return !!resolveDataSource();
  }

  async function search(query: string): Promise<NormalizedOption[]> {
    const doPrefetch = config.prefetch && searchCount === 0;
    const effectiveQuery = doPrefetch
      ? hasDataSource()
        ? '*'
        : ''
      : (query ?? '');

    searchCount++;

    if (hasDataSource()) {
      if (!doPrefetch && !effectiveQuery) {
        return [];
      }

      const dataSource = resolveDataSource();
      if (!dataSource) {
        return [];
      }

      const parameters = {
        ...(dataSource.parameters || {}),
        query: effectiveQuery,
      };

      if (!config.request) {
        console.warn(
          '[useItpSelectProvider] dataSource provided, but no request() function set.',
        );
        return [];
      }

      try {
        const response = await config.request(dataSource.name, parameters);
        const data = response?.data || response || [];
        return (Array.isArray(data) ? data : []).map(toNormalized);
      } catch (error) {
        console.error(
          '[useItpSelectProvider] dataSource request failed:',
          error,
        );
        return [];
      }
    }

    if (isAsyncOptions()) {
      const optionsFn = effectiveOptions as AsyncOptionsFn;
      try {
        const data = await optionsFn(effectiveQuery);
        return (Array.isArray(data) ? data : []).map(toNormalized);
      } catch (error) {
        console.error('[useItpSelectProvider] async options failed:', error);
        return [];
      }
    }

    const list = Array.isArray(effectiveOptions) ? effectiveOptions : [];

    if (!effectiveQuery || effectiveQuery === '*') {
      return list.map(toNormalized);
    }

    const searchString = effectiveQuery.toLowerCase();
    const tf = config.textField ?? 'text';
    return list
      .filter((item) => {
        const label =
          item !== null && typeof item === 'object'
            ? String(getByPath(item, tf) ?? '')
            : String(item ?? '');
        return label.toLowerCase().includes(searchString);
      })
      .map(toNormalized);
  }

  function loadInitial(): NormalizedOption[] {
    if (config.initialOptions && config.initialOptions.length > 0) {
      return config.initialOptions.map(toNormalized);
    }

    if (Array.isArray(effectiveOptions)) {
      return effectiveOptions.map(toNormalized);
    }

    return [];
  }

  function byValue(currentValue: any): NormalizedOption | null {
    const vf = config.valueField ?? 'value';

    function matchItem(item: any): boolean {
      if (item === null || typeof item !== 'object') {
        return item === currentValue;
      }
      return getByPath(item, vf) === currentValue;
    }

    if (config.initialOptions && config.initialOptions.length > 0) {
      const match = config.initialOptions.find(matchItem);
      if (match) {
        return toNormalized(match);
      }
    }

    if (Array.isArray(effectiveOptions)) {
      const match = effectiveOptions.find(matchItem);
      if (match) {
        return toNormalized(match);
      }
    }

    return null;
  }

  function isAsync(): boolean {
    return isAsyncOptions() || hasDataSource();
  }

  return {
    search,
    loadInitial,
    byValue,
    toNormalized,
    isAsync,
  };
}
